<script lang="ts" module>
  /** What the button MEANS, which is not the same as what it looks like.
   *
   *  `on` is deliberately an intent rather than a separate `pressed` prop: in
   *  this app "armed" is the loudest thing a control can say, and it has to be
   *  impossible to write a button that is armed and does not look it. Pass
   *  `aria-pressed` alongside it — the class is the picture, the attribute is
   *  the fact. */
  export type ButtonIntent = 'default' | 'primary' | 'on' | 'danger' | 'ghost';
  export type ButtonSize = 'sm' | 'md';
</script>

<script lang="ts">
  /* One button, for every button.
   *
   * ═══ WHAT THIS REPLACES ═══
   *
   * Counted across apps/web and this package: `.open` in App.svelte, `.slot` in
   * Dock.svelte, `.tool` in ChartTools.svelte, `.acts button` in
   * RouteContent.svelte, `.steps button` in TimeStrip.svelte and `.now` in
   * PassageControls.svelte. Six rulesets, in six files, describing the same
   * button at 7/9/10/11/12 px of padding and 32/34/44/46 px of height.
   *
   * None of them was wrong on its own. Together they are why two panels side by
   * side never quite looked like one product.
   *
   * ═══ WHY `primary` IS FILLED AND `on` IS NOT ═══
   *
   * Outlined teal already means ARMED — a live ruler waiting for a tap, a lit
   * layer, the parked hour cell. A control that looks armed when it is merely
   * available is a mode you cannot read, and this app has exactly one word for
   * "on". So the call to action takes the fill and the state keeps the outline.
   */
  /* ═══ THE PROPS ARE ENUMERATED, NOT SPREAD ═══
   *
   * `{...rest}` with `HTMLButtonAttributes` is the ergonomic way to write this
   * and it is not available here. `vite.config.ts` compiles this package a
   * second time with `customElement: true` to produce the embeddable bundle,
   * and under that flag Svelte cannot infer which properties to expose for a
   * component with a rest element — `svelte-check` says so, once per build.
   * Nothing breaks, but a permanent warning in `npm run check` is a signal
   * nobody reads after the third time.
   *
   * A bounded API is the better answer anyway: a design-system button that
   * accepts arbitrary attributes accepts `style`, and the whole point of this
   * file is that there is one place a button's padding is decided.
   */
  import type { Snippet } from 'svelte';

  let {
    intent = 'default',
    size = 'md',
    /** Square. Sets a WIDTH floor as well as a height — "3D" is two characters
     *  and measured 34 px wide against Apple's 44, and a target that is tall
     *  enough and too thin is still a target you miss. */
    icon = false,
    /** Full width. For the one pinned action at the foot of a panel. */
    block = false,
    disabled = false,
    /** `button`, always, unless it is genuinely inside a form. Svelte does not
     *  default this and a bare <button> in a form submits it. */
    type = 'button',
    title,
    /** Required whenever the label is a glyph. Every icon button in this app is
     *  a symbol somebody has to guess at otherwise. */
    ariaLabel,
    /** The FACT behind `intent="on"`. The class is the picture; this is what a
     *  screen reader gets. */
    ariaPressed,
    ariaExpanded,
    onclick,
    children
  }: {
    intent?: ButtonIntent;
    size?: ButtonSize;
    icon?: boolean;
    block?: boolean;
    disabled?: boolean;
    type?: 'button' | 'submit' | 'reset';
    title?: string;
    ariaLabel?: string;
    ariaPressed?: boolean;
    ariaExpanded?: boolean;
    onclick?: (e: MouseEvent) => void;
    children: Snippet;
  } = $props();
</script>

<button
  class="btn {intent} {size}"
  class:icon
  class:block
  {type}
  {disabled}
  {title}
  {onclick}
  aria-label={ariaLabel}
  aria-pressed={ariaPressed}
  aria-expanded={ariaExpanded}
>
  {@render children()}
</button>

<style>
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--sp-3);
    min-height: var(--h-md);
    padding: 0 var(--sp-5);
    border: 1px solid var(--line);
    border-radius: var(--r-2);
    background: transparent;
    color: var(--ink);
    font: var(--fs-body) / 1 var(--mono);
    letter-spacing: var(--tr-ui);
    cursor: pointer;
    transition:
      border-color var(--dur-fast) var(--ease-out),
      color var(--dur-fast) var(--ease-out),
      background var(--dur-fast) var(--ease-out);
  }
  .btn:hover:not(:disabled) {
    border-color: var(--sub);
  }
  .btn:focus-visible {
    outline: 2px solid var(--cy);
    outline-offset: 2px;
  }
  .btn:disabled {
    opacity: 0.4;
    cursor: default;
  }

  .primary {
    background: var(--cy);
    border-color: var(--cy);
    color: var(--panel);
  }
  .primary:hover:not(:disabled) {
    background: color-mix(in srgb, var(--cy) 84%, var(--ink));
    border-color: color-mix(in srgb, var(--cy) 84%, var(--ink));
  }

  .on {
    border-color: var(--cy);
    color: var(--cy);
    background: var(--cy-weak);
  }

  /* Outlined, not filled. A filled red button is the largest thing on a panel
     and DELETE POINT does not deserve that weight. */
  .danger {
    border-color: var(--red-line);
    color: var(--red);
  }
  .danger:hover:not(:disabled) {
    background: var(--red-weak);
    border-color: var(--red);
  }

  /* The third and fourth actions in a row — undo, cancel, close. */
  .ghost {
    border-color: transparent;
    color: var(--sub);
  }
  .ghost:hover:not(:disabled) {
    border-color: var(--line);
    color: var(--ink);
  }

  .sm {
    min-height: var(--h-sm);
    padding: 0 var(--sp-4);
    font: var(--fs-label) / 1 var(--px);
    letter-spacing: var(--tr-label);
  }

  .icon {
    min-width: var(--h-md);
    padding: 0 var(--sp-3);
  }
  .block {
    width: 100%;
  }

  /* Apple's floor, applied by POINTER KIND rather than by screen width — a
     touchscreen laptop and an iPad both want it and neither is narrow. */
  @media (pointer: coarse) {
    .btn {
      min-height: var(--h-touch);
      padding: 0 var(--sp-6);
    }
    .icon {
      min-width: var(--h-touch);
    }
    .sm {
      min-height: var(--h-touch);
    }
  }
</style>
